import type { ReactNode } from "react";
import { Menu } from "lucide-react";
import { parquePath, parques, type ParqueFoto } from "@/content/parques";
import { CAMPING_YEAR, LAST_UPDATED_DISPLAY, LAST_UPDATED_ISO } from "@/constants";
import "./site.css";

/**
 * Site shell (DESIGN.md): Figtree, nav and footer, everything scoped under
 * .pq. Used by the home page and /parques. Light only (no dark mode).
 */

// Rendered in <body>: valid HTML for a stylesheet, and it keeps Figtree off
// the pages not yet migrated (index.html already preconnects to Google Fonts).
const FIGTREE =
  "https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600&display=swap";

// Root-relative hrefs, not bare hashes, so they also work from /parques.
const NAV_LINKS = [
  { label: "Campings", href: "/#campsites" },
  { label: "Preguntas", href: "/#faq" },
  { label: "Parques", href: "/parques" },
];

/**
 * "Cómo visitar" is the one nav button on every page. It goes to the home
 * page's section unless the page passes its own (park pages: "Cómo llegar").
 */
const NAV_CTA = { href: "/#tourism-info", label: "Cómo visitar", narrow: "Visitar" };

/** Footer keeps every Tayrona section, including the ones not in the nav. */
const FOOTER_TAYRONA = [
  { label: "Campings", href: "/#campsites" },
  { label: "Cierres", href: "/#cierres" },
  { label: "Qué llevar", href: "/#lista-equipaje" },
  { label: "Preguntas", href: "/#faq" },
  { label: "Cómo visitar", href: "/#tourism-info" },
];

// Dynamically imported so the footer, on every page, doesn't pull
// vanilla-cookieconsent into the main chunk (see src/main.tsx).
const openConsentPreferences = () => {
  import("@/lib/consent").then(({ showConsentPreferences }) => showConsentPreferences());
};

const PARQUE_OFICIAL = "https://www.parquesnacionales.gov.co/nuestros-parques/pnn-tayrona/";

export function SiteLayout({
  current,
  tema,
  ctaHref,
  children,
}: {
  /** Which nav item is the current page, if any (none on the 404). */
  current?: "home" | "parques";
  tema?: string;
  /** Section on this page for the nav button; defaults to the home page's. */
  ctaHref?: string;
  children: ReactNode;
}) {
  const cta = { ...NAV_CTA, href: ctaHref ?? NAV_CTA.href };

  return (
    <div className={`pq${tema ? ` pq-tema-${tema}` : ""}`}>
      <link rel="stylesheet" href={FIGTREE} />
      <a href="#main" className="skip-link">Saltar al contenido</a>

      <header className="pq-nav">
        <div className="pq-wrap pq-nav-inner">
          <a href="/" className="pq-wordmark">Camping Parque Tayrona</a>
          <nav aria-label="Principal" className="pq-nav-links">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                aria-current={current === "parques" && l.href === "/parques" ? "page" : undefined}
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="pq-nav-end">
            <a href={cta.href} className="pq-btn pq-btn-primary pq-btn-sm">
              {/* One element, so the button's flex gap can't split the label. */}
              <span>
                <span className="pq-show-narrow" aria-hidden="true">{cta.narrow}</span>
                <span className="pq-hide-narrow">{cta.label}</span>
              </span>
            </a>
            {/* Native disclosure: works without JS and in the prerendered HTML. */}
            <details className="pq-menu">
              <summary aria-label="Abrir menú">
                <Menu size={20} aria-hidden="true" />
              </summary>
              <nav aria-label="Menú" className="pq-menu-panel">
                {NAV_LINKS.map((l) => (
                  <a key={l.href} href={l.href}>{l.label}</a>
                ))}
              </nav>
            </details>
          </div>
        </div>
      </header>

      <main id="main">{children}</main>

      <footer className="pq-footer">
        <div className="pq-wrap">
          <div className="pq-footer-grid">
            <div>
              <a href="/" className="pq-wordmark">Camping Parque Tayrona</a>
              <p className="pq-small" style={{ maxWidth: "38ch", marginTop: 12 }}>
                Guía local e independiente de campings en el Parque Nacional
                Natural Tayrona y de otros parques de Colombia. Sin comisiones ni
                afiliados.
              </p>
            </div>
            <div>
              <h2>Tayrona</h2>
              <ul>
                {FOOTER_TAYRONA.map((l) => (
                  <li key={l.href}><a href={l.href}>{l.label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h2>Otros parques</h2>
              <ul>
                {parques.map((p) => (
                  <li key={p.slug}>
                    <a href={parquePath(p)}>{p.atractivoPrincipal}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2>Parque Nacional</h2>
              <ul>
                <li><span className="pq-footer-text">Km 34, vía Santa Marta a Riohacha</span></li>
                <li><a href="tel:+5754230404">+57 (5) 423-0404</a></li>
                <li>
                  <a href={PARQUE_OFICIAL} target="_blank" rel="noopener noreferrer">
                    parquesnacionales.gov.co
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pq-footer-base">
            <p>
              © {CAMPING_YEAR} Camping Parque Tayrona · Última actualización:{" "}
              <time dateTime={LAST_UPDATED_ISO}>{LAST_UPDATED_DISPLAY}</time> · Guía
              independiente, sin afiliación con Parques Nacionales Naturales de
              Colombia. Antes de viajar, confirma tarifas y cierres en el sitio oficial.
            </p>
            <nav aria-label="Legal" className="pq-footer-legal">
              <a href="/politica-cookies">Política de cookies</a>
              <button type="button" onClick={openConsentPreferences}>
                Gestionar cookies
              </button>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Wikimedia credit: author, license, link. Required by CC BY / BY-SA. */
export function FotoCredito({ foto }: { foto: ParqueFoto }) {
  return (
    <>
      Foto:{" "}
      <a href={foto.url} target="_blank" rel="noopener noreferrer">
        {foto.autor}
      </a>
      , {foto.licencia}
    </>
  );
}

/** Photo for PhotoHero. `credit` is plain text or a FotoCredito element. */
export interface HeroFoto {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

/** Full-bleed photo that fades into the white page, with floating facts. */
export function PhotoHero({
  foto,
  credit,
  chips,
}: {
  foto: HeroFoto;
  /** Optional: only when the author is known (CC photos must show it). */
  credit?: ReactNode;
  /** Optional floating facts. The /parques heroes use none (her call). */
  chips?: ReactNode;
}) {
  return (
    <div className="pq-hero">
      <img
        src={foto.src}
        alt={foto.alt}
        width={foto.width}
        height={foto.height}
        // Lowercase on purpose: React 18 doesn't know the camelCase prop.
        {...{ fetchpriority: "high" }}
        decoding="async"
      />
      {credit ? <p className="pq-hero-credit">{credit}</p> : null}
      {chips ? <div className="pq-hero-chips pq-float">{chips}</div> : null}
    </div>
  );
}
